import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/ui/primitives/sheet";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

export type LGoSheetSide = "left" | "right" | "top" | "bottom";
export type LGoSheetWidth = "sm" | "md" | "lg";

const widthClass: Record<LGoSheetWidth, string> = {
  sm: "sm:max-w-md",
  md: "sm:max-w-xl",
  lg: "sm:max-w-2xl",
};

export interface LGoSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  side?: LGoSheetSide;
  width?: LGoSheetWidth;
  scrollable?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  loading?: boolean;
  className?: string;
}

export function LGoSheet({
  open,
  onOpenChange,
  title,
  description,
  side = "right",
  width = "md",
  scrollable = true,
  children,
  footer,
  onSubmit,
  loading,
  className,
}: LGoSheetProps) {
  const body = (
    <div
      className={cn(
        // min-h-0: flex items default to min-height:auto, which lets the body
        // grow past the sheet and push the footer off screen.
        "flex-1 min-h-0",
        scrollable && "overflow-y-auto",
        loading && "opacity-60 pointer-events-none",
      )}
    >
      {children}
    </div>
  );

  const content = onSubmit ? (
    <form onSubmit={onSubmit} className="flex flex-1 min-h-0 flex-col">
      {body}
      {footer && (
        <div className="mt-4 flex shrink-0 items-center justify-end gap-2 border-t border-border pt-4">
          {footer}
        </div>
      )}
    </form>
  ) : (
    <div className="flex flex-1 min-h-0 flex-col">
      {body}
      {footer && (
        <div className="mt-4 flex shrink-0 items-center justify-end gap-2 border-t border-border pt-4">
          {footer}
        </div>
      )}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side={side} className={cn(widthClass[width], "flex flex-col", className)}>
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight">{title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        {content}
      </SheetContent>
    </Sheet>
  );
}
